import * as Cookies from "es-cookie";

/**
 * Cookie writes with security options applied by default.
 *
 * Every cookie in this app was previously written with `Cookies.set(name, value)`
 * and no options at all — no `secure`, no `sameSite`, no expiry. That leaves
 * session tokens and identity readable by any script on the page and sendable
 * over plain http. This wrapper is the one place those defaults live.
 *
 * Not covered here, on purpose:
 *   - httpOnly. The token is read by JS to build the Authorization header, so it
 *     cannot be httpOnly without moving auth to a server-set cookie. That is a
 *     separate transport change.
 *   - Trusting client-stored authorization. student_userlevel is read in ~140
 *     places and can still be edited in devtools; the real fix is server-side
 *     enforcement, not storage flags. This only limits exposure and lifetime.
 */

/**
 * `secure` restricts the cookie to https. On http://localhost that would stop
 * the cookie being stored at all, so it is switched off there and on only for a
 * real https origin — which is every deployed environment.
 */
function isSecureContext() {
  return typeof window !== "undefined" &&
    window.location &&
    window.location.protocol === "https:";
}

/**
 * No `expires` default: the existing cookies had none, making them session
 * cookies that die on browser close. That is a reasonable lifetime — adding a
 * multi-day expiry here would make tokens outlive the session, worse on a shared
 * machine. Pass { expires: N } explicitly where persistence is genuinely wanted.
 *
 * @param {string} name
 * @param {*}      value
 * @param {object} [opts]  overrides — e.g. { sameSite: 'Strict' } to tighten one.
 */
export function setSecureCookie(name, value, opts = {}) {
  Cookies.set(name, value, {
    secure: isSecureContext(),
    sameSite: "Lax",   // sent on top-level navigation, blocked on cross-site POST
    path: "/",
    ...opts,
  });
}

// Re-export get/remove so a file can import everything from here and drop its
// direct es-cookie import.
export const getCookie = Cookies.get;
export const removeCookie = Cookies.remove;

export default setSecureCookie;
